import {
  Box,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Search as SearchIcon, ViewList as ListIcon } from '@mui/icons-material';
import { useMemo, useState } from 'react';
import useSWR from 'swr';
import { apiMaster } from '@/api/platform';
import { DataTable } from '@/components/DataTable';
import { MetricStat } from '@/components/MetricStat';
import { StatusPill } from '@/components/StatusPill';
import { PageShell } from '@/layout/PageShell';
import type { ApiMaster } from '@/types/backend';

/**
 * Read view of the {@code api_master} catalog. The collection is the
 * single source of truth for every endpoint the connector exposes,
 * grouped by milestone (AUTH, M1, M2, M3, ADMIN, TENANT).
 *
 * Tabs across the top filter by milestone — the "All" tab shows every
 * row plus a free-text search across apiCode / name / pathPattern. Per-
 * tenant access overrides (TenantApiAccess) are wired on the backend
 * but the toggle UI for them is a follow-up; this page is currently
 * read-only so an operator can audit what the platform exposes.
 */

const METHOD_TONES: Record<string, 'success' | 'info' | 'warning' | 'danger' | 'mono'> = {
  GET:    'info',
  POST:   'success',
  PUT:    'warning',
  PATCH:  'warning',
  DELETE: 'danger',
};

export function ApiMastersPage() {
  const theme = useTheme();
  const [activeMilestone, setActiveMilestone] = useState<string>('ALL');
  const [search, setSearch] = useState('');

  const { data, error, isLoading } = useSWR(
    '/api/abdm/admin/api-master',
    () => apiMaster.list(),
  );

  const groupedCount = useMemo(() => {
    const map = new Map<string, number>();
    (data ?? []).forEach((r) => map.set(r.milestone, (map.get(r.milestone) ?? 0) + 1));
    return map;
  }, [data]);

  const milestones = useMemo(() => {
    const s = new Set<string>(['ALL']);
    (data ?? []).forEach((r) => s.add(r.milestone));
    return [...s];
  }, [data]);

  const filtered = useMemo(() => {
    const lc = search.trim().toLowerCase();
    return (data ?? [])
      .filter((r) => activeMilestone === 'ALL' || r.milestone === activeMilestone)
      .filter((r) =>
        !lc ||
        r.apiCode.toLowerCase().includes(lc) ||
        r.name.toLowerCase().includes(lc) ||
        (r.pathPattern ?? '').toLowerCase().includes(lc)
      )
      .sort((a, b) => a.apiCode.localeCompare(b.apiCode));
  }, [data, activeMilestone, search]);

  const total = data?.length ?? 0;
  const active = data?.filter((r) => r.status === 'ACTIVE').length ?? 0;
  const defaultEnabled = data?.filter((r) => r.defaultEnabled).length ?? 0;

  return (
    <PageShell
      kicker="PLATFORM · API MASTERS"
      title="API Masters"
      subtitle="Canonical catalog of every endpoint the connector exposes, grouped by milestone. Drives per-tenant access rules."
      width="wide"
    >
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' }, gap: 2.5, mb: 3 }}>
        <MetricStat label="Total APIs" value={isLoading ? '…' : total} />
        <MetricStat label="Active" value={isLoading ? '…' : active} tone="success" hint="status=ACTIVE" />
        <MetricStat label="Default-on" value={isLoading ? '…' : defaultEnabled} tone="info" hint="every tenant gets these" />
      </Box>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }} alignItems={{ md: 'center' }}>
        <Tabs
          value={activeMilestone}
          onChange={(_, v: string) => setActiveMilestone(v)}
          sx={{
            minHeight: 36,
            '& .MuiTab-root': { minHeight: 36, textTransform: 'none', fontWeight: 500, fontSize: 13 },
          }}
        >
          {milestones.map((m) => (
            <Tab
              key={m}
              value={m}
              label={
                <Stack direction="row" spacing={1} alignItems="center">
                  <Box>{m}</Box>
                  <Box
                    sx={{
                      px: 0.75,
                      py: 0.125,
                      borderRadius: theme.radius.pill + 'px',
                      bgcolor: theme.palette.surface.sunken,
                      color: 'text.tertiary',
                      fontSize: 11,
                      fontWeight: 600,
                    }}
                  >
                    {m === 'ALL' ? total : (groupedCount.get(m) ?? 0)}
                  </Box>
                </Stack>
              }
            />
          ))}
        </Tabs>
        <TextField
          size="small"
          label="Search"
          placeholder="apiCode, name, path…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ minWidth: 280, ml: { md: 'auto' } }}
          InputProps={{ startAdornment: <SearchIcon sx={{ fontSize: 18, mr: 1, color: 'text.tertiary' }} /> }}
        />
      </Stack>

      <DataTable<ApiMaster>
        rows={filtered}
        rowKey={(r) => r.apiCode}
        isLoading={isLoading}
        error={error ? { message: (error as Error).message } : null}
        searchable
        searchPlaceholder="apiCode, name, path…"
        paginated
        empty={{
          icon: ListIcon,
          title: search ? 'No APIs match your search.' : 'No APIs registered yet.',
          description: search ? 'Loosen the search.' : 'The api_master catalog is empty — run ApiMasterSeeder.',
        }}
        columns={[
          {
            key: 'apiCode',
            header: 'API code',
            sortable: true,
            sortValue: (r) => r.apiCode,
            pinned: true,
            cell: (r) => (
              <Typography variant="body2" sx={{ fontFamily: 'JetBrains Mono, monospace', fontWeight: 500, fontSize: 12 }}>
                {r.apiCode}
              </Typography>
            ),
            width: 220,
          },
          {
            key: 'name',
            header: 'Name',
            sortable: true,
            sortValue: (r) => r.name,
            searchValue: (r) => `${r.name} ${r.description ?? ''}`,
            cell: (r) => (
              <Stack spacing={0}>
                <Typography variant="body2" fontWeight={500}>{r.name}</Typography>
                {r.description && <Typography variant="caption" color="text.tertiary">{r.description}</Typography>}
              </Stack>
            ),
          },
          {
            key: 'method',
            header: 'Method',
            sortable: true,
            sortValue: (r) => r.httpMethod,
            filter: { type: 'select', getFilterValue: (r) => r.httpMethod },
            cell: (r) => (
              <StatusPill tone={METHOD_TONES[r.httpMethod] ?? 'mono'} dot={false}>
                {r.httpMethod}
              </StatusPill>
            ),
            width: 100,
          },
          {
            key: 'path',
            header: 'Path',
            sortable: true,
            sortValue: (r) => r.pathPattern,
            filter: { type: 'text' },
            cell: (r) => (
              <Typography variant="caption" sx={{ fontFamily: 'JetBrains Mono, monospace', wordBreak: 'break-all' }}>
                {r.pathPattern}
              </Typography>
            ),
          },
          {
            key: 'milestone',
            header: 'Milestone',
            sortable: true,
            sortValue: (r) => r.milestone,
            filter: { type: 'select', getFilterValue: (r) => r.milestone },
            cell: (r) => (
              <StatusPill
                tone={
                  r.milestone === 'M1' ? 'info'
                  : r.milestone === 'M2' ? 'warning'
                  : r.milestone === 'M3' ? 'success'
                  : r.milestone === 'AUTH' ? 'mono'
                  : 'mono'
                }
                dot={false}
              >
                {r.milestone}
              </StatusPill>
            ),
            width: 110,
          },
          {
            key: 'default',
            header: 'Default',
            sortable: true,
            sortValue: (r) => (r.defaultEnabled ? 1 : 0),
            filter: {
              type: 'select',
              options: [{ label: 'on', value: 'on' }, { label: 'off', value: 'off' }],
              getFilterValue: (r) => (r.defaultEnabled ? 'on' : 'off'),
            },
            cell: (r) => r.defaultEnabled
              ? <StatusPill tone="success">on</StatusPill>
              : <StatusPill tone="mono">off</StatusPill>,
            width: 100,
          },
        ]}
      />
    </PageShell>
  );
}
